import {
  Box,
  Paper,
  Typography,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useOrg } from '../../organization/useOrg';
import { useState, useEffect } from 'react';
import { useGetProject } from '@/queries/useProjects';
import { useParams, useNavigate } from 'react-router-dom';
import { MultiSelect } from '@/tools/ManagerialComponents';
import { useDepartmentService } from '@/api/services/departmentService';
import { useQuery } from '@tanstack/react-query';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import { fetchOrganizationUsers } from '@/services/userService';
import { usePencilsDown } from '@/decarbonization/hooks/usePencilsDown';
import { BaseDecarbFooterBar } from '@/decarbonization/components/BaseDecarbFooterBar';
import { API_ENDPOINTS } from '@/api/endpoints';
import { apiFetch } from '@/util/apiClient';
import { Project } from '@/stores/ProjectStore';
import { useQueryClient } from '@tanstack/react-query';

export function ProjectManagers() {
  const { selectedOrg } = useOrg();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const pencilsDown = usePencilsDown();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: project } = useGetProject(selectedOrg?.id, projectId);
  const departmentService = useDepartmentService();
  const [sponsoringDeptIds, setSponsoringDeptIds] = useState<string[]>([]);
  const [overseeingDeptIds, setOverseeingDeptIds] = useState<string[]>([]);
  const [usingDeptIds, setUsingDeptIds] = useState<string[]>([]);
  const [progManagerIds, setProgManagerIds] = useState<string[]>([]);
  const [projManagerIds, setProjManagerIds] = useState<string[]>([]);
  const [budgetOfficerIds, setBudgetOfficerIds] = useState<string[]>([]);

  useEffect(() => {
    if (project) {
      setSponsoringDeptIds(project.sponsoringDepts || []);
      setOverseeingDeptIds(project.overseeingDepts || []);
      setUsingDeptIds(project.usingDepts || []);
      setProgManagerIds(project.programManagers || []);
      setProjManagerIds(project.projectManagers || []);
      setBudgetOfficerIds(project.budgetOfficers || []);
    }
  }, [project]);

  // get department / user options for managerial page
  const departmentsQuery = useQuery({
    queryKey: ['departments', selectedOrg?.id],
    queryFn: async () => {
      const response = await departmentService.list();
      return response.data; // Extract data from ApiResponse
    },
    enabled: !!selectedOrg,
  });

  const availableDepartments: Department[] = departmentsQuery.data || [];

  const usersQuery = useQuery({
    queryKey: [
      'organization-users',
      selectedOrg?.id,
      1,
      '', // search
      false, // showArchived
      '', // department
    ],
    queryFn: () =>
      fetchOrganizationUsers({
        organizationId: selectedOrg?.id || '',
        page: 1,
        search: '',
        showArchived: false,
        department: '',
      }),
    enabled: !!selectedOrg,
  });
  const availableMembers: Member[] = usersQuery.data?.results || [];

  const handleNextClick = async () => {
    if (project === undefined || selectedOrg === null) return null;
    try {
      // Prepare project data
      const projectData = {
        ...project,
        overseeingDepts: overseeingDeptIds,
        sponsoringDepts: sponsoringDeptIds,
        usingDepts: usingDeptIds,
        programManagers: progManagerIds,
        projectManagers: projManagerIds,
        budgetOfficers: budgetOfficerIds,
      };
      let id = undefined;
      id = await apiFetch<Project>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(selectedOrg.id, project.id),
        {
          method: 'PUT',
          body: JSON.stringify({ ...projectData, id: project.id }),
        },
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project', selectedOrg.id, id.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['departments', selectedOrg?.id],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'organization-users',
            selectedOrg?.id,
            1,
            '', // search
            false, // showArchived
            '', // department
          ],
        }),
      ]);
      navigate('../proj-details');
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleBackClick = () => {
    // hit the back button, so do not save changes
    navigate('../proj-overview');
    return;
  };

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Title of page */}
        <Box
          sx={{
            mt: '35px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            pointerEvents: 'auto',
            ml: '70px',
            flexShrink: 0,
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component='h1'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(24px, 1.85vw, 35px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
              mb: 'clamp(4px, 0.4vw, 8px)',
            }}
          >
            Project Creation
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component='h2'
            sx={{
              fontFamily: 'Futura PT, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(45px, 3.5vw, 68px)',
              color: 'rgba(40, 40, 40, 1)',
              textShadow: {
                xs: '-2px 2px rgba(101, 166, 178, 1)',
                lg: '-4px 3px rgba(101, 166, 178, 1)',
              },
              textTransform: 'uppercase',
              letterSpacing: 'clamp(8px, 0.75vw, 14px)',
              lineHeight: 1,
            }}
          >
            {project?.name || 'New Project'}
          </Typography>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            width: '97%',
            my: 5,
            px: 10,
            minHeight: 0,
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column !important',
          }}
        >
          <Paper
            sx={{
              bgcolor: 'primary.dark',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant={'h6'}>Managerial Overview</Typography>
              <Typography variant={'subtitle1'} sx={{ mt: '3px' }}>
                Assign managers to your project
              </Typography>
            </Box>
          </Paper>

          <Box
            sx={{
              bgcolor: 'primary.light',
              p: 6,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
              overflow: 'auto',
              flexGrow: 1,
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            <Grid
              container
              direction='row'
              rowSpacing={4}
              columnSpacing={'4rem'}
              sx={{ p: 10, justifyContent: 'center' }}
            >
              <MultiSelect
                title={'Program Manager(s)'}
                onAdd={(selectedId: string) => {
                  setProgManagerIds([...progManagerIds, selectedId]);
                }}
                onRemove={(selectedId: string) =>
                  setProgManagerIds(
                    progManagerIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableMembers.filter(
                  (member) => member.role === 'Program Manager',
                )}
                selectedIds={progManagerIds}
                isDepartment={false}
              />
              <MultiSelect
                title={'Budget Officer(s)'}
                onAdd={(selectedId: string) =>
                  setBudgetOfficerIds([...budgetOfficerIds, selectedId])
                }
                onRemove={(selectedId: string) =>
                  setBudgetOfficerIds(
                    budgetOfficerIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableMembers.filter(
                  (member) => member.role === 'Budget Manager',
                )}
                selectedIds={budgetOfficerIds}
                isDepartment={false}
              />
              <MultiSelect
                title={'Project Manager(s)'}
                onAdd={(selectedId: string) =>
                  setProjManagerIds([...projManagerIds, selectedId])
                }
                onRemove={(selectedId: string) =>
                  setProjManagerIds(
                    projManagerIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableMembers}
                selectedIds={projManagerIds}
                isDepartment={false}
              />
              <MultiSelect
                title={'Sponsoring Department(s)'}
                onAdd={(selectedId: string) =>
                  setSponsoringDeptIds([...sponsoringDeptIds, selectedId])
                }
                onRemove={(selectedId: string) =>
                  setSponsoringDeptIds(
                    sponsoringDeptIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableDepartments}
                selectedIds={sponsoringDeptIds}
                isDepartment={true}
              />
              <MultiSelect
                title={'Overseeing Department(s)'}
                onAdd={(selectedId: string) =>
                  setOverseeingDeptIds([...overseeingDeptIds, selectedId])
                }
                onRemove={(selectedId: string) =>
                  setOverseeingDeptIds(
                    overseeingDeptIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableDepartments}
                selectedIds={overseeingDeptIds}
                isDepartment={true}
              />
              <MultiSelect
                title={'Using Department(s)'}
                onAdd={(selectedId: string) =>
                  setUsingDeptIds([...usingDeptIds, selectedId])
                }
                onRemove={(selectedId: string) =>
                  setUsingDeptIds(
                    usingDeptIds.filter((id) => id !== selectedId),
                  )
                }
                dropdownOptions={availableDepartments}
                selectedIds={usingDeptIds}
                isDepartment={true}
              />
            </Grid>
          </Box>
        </Container>
      </Box>

      <BaseDecarbFooterBar
        onBackClick={handleBackClick}
        // will need to route to subprojects page eventually
        onNextClick={handleNextClick}
        nextIsDisabled={false}
        nextIsDisabledText={'INCOMPLETE DATA'}
        onReturnToHubClick={handleNextClick}
        returnText={
          pencilsDown ? 'RETURN TO SUMMARY' : 'SAVE & EXIT TO SUMMARY'
        }
      />
    </>
  );
}
